import moment from "moment";
import hashObject from "object-hash";

import {
  computeRelativeDate,
  tryParseRelativeDateOffsetValue,
} from "../../datetime.js";
import { DATE_FORMAT_NO_TIME } from "../../datetimeType.js";
import {
  columnFilterFullyConfigured,
  displayTableFilterToFilterCellFilter,
} from "../../display-table/columnFilterUtils.js";
import { ColumnFilter } from "../../display-table/filterTypes.js";
import { FilledDynamicValueTableColumnType } from "../../DynamicValue.js";
import { assertNever } from "../../errors.js";
import { getExploreFieldName } from "../../explore/exploreFieldUtils.js";
import {
  getLumpPredicateString,
  resolveLumpOrderBy,
} from "../../explore/exploreLumpUtils.js";
import { ExploreFieldType, ExploreSpec } from "../../explore/types.js";
import {
  HqlAggregationBuilder,
  HqlNextTemporaryParamMapping,
  HqlSelectionBuilder,
  isAggregationBuilder,
} from "../../hql/HqlNextSpecBuilder.js";
import { columnTypeToCalciteType } from "../../hql/types.js";
import {
  DisplayTableColumnId,
  HexSLTypes,
  LumpColumnPredicate,
  columnTypeToHqlNextType,
  getDatasetPathForField,
  typedObjectEntries,
  typedObjectFromEntries,
} from "../../index.js";
import { notNullish } from "../../filterGuards.js";
import {
  Filter,
  FilterCellBinaryColumnPredicate,
  FilterCellListBinaryColumnPredicate,
  FilterCellLumpColumnPredicate,
  FilterCellUnaryColumnPredicate,
  FilterGroup,
} from "../filterTypes.js";
import {
  DATE_OPERATOR_MAP,
  escapeSingleQuotes,
  filterFullyConfigured,
  getSingleJinjaVariableReference,
} from "../generateSql.js";

import {
  DateFilterMode,
  DateFilterTransformations,
  getDateFilterTransformations,
} from "./dateFilterTransformations.js";

export type FilterHqlOptions = {
  dateFilterMode?: DateFilterMode;
};

/**
 * Result of formatting a filter argument for parameterized SQL. `formatted`
 * is the Python/Jinja string-literal source used by the non-HISC path (it
 * gets embedded inside a `{{ ... }}` expression and evaluated by JinjaSql);
 * `literal` is the same value as a typed JS-native scalar, consumed by HISC.
 */
type FormattedFilterArg = {
  formatted: string;
  literal: string | number | boolean;
};

export function maybeWrapQuotes({
  arg: rawArg,
  columnType,
}: {
  arg: string;
  columnType: FilledDynamicValueTableColumnType;
}): FormattedFilterArg {
  if (columnType === "NUMBER") {
    return { formatted: rawArg, literal: Number(rawArg) };
  } else if (columnType === "BOOLEAN") {
    const lower = rawArg.toLowerCase().trim();
    if (lower === "true" || lower === "false") {
      return { formatted: rawArg.trim(), literal: lower === "true" };
    }
  }
  return {
    formatted: `'${escapeSingleQuotes(rawArg)}'`,
    literal: rawArg,
  };
}

function formatArg({
  arg: rawArg,
  columnType,
}: {
  arg: string;
  columnType: FilledDynamicValueTableColumnType;
}): FormattedFilterArg {
  // handle relative dates
  if (
    columnType === "DATE" ||
    columnType === "DATETIME" ||
    columnType === "DATETIMETZ" ||
    // If the column type is unknown and the predicate is not a date type, then
    // we will just treat this as non date/datetime column type
    columnType === "UNKNOWN"
  ) {
    const m = moment(rawArg);
    if (m.isValid() || rawArg === "") {
      let formattedArg = rawArg;
      if (columnType === "DATE") {
        // if for whatever reason a timestamp arg is specified for a date column
        // type, this ensures that the arg is the right format.
        formattedArg = m.format(DATE_FORMAT_NO_TIME);
      }
      return { formatted: `"${formattedArg}"`, literal: formattedArg };
    } else {
      const parsedRelativeDate = tryParseRelativeDateOffsetValue(rawArg);
      if (parsedRelativeDate == null) {
        if (columnType === "UNKNOWN") {
          return maybeWrapQuotes({ arg: rawArg, columnType });
        }
        throw Error("Invalid date filter argument");
      }
      const computedDate =
        computeRelativeDate(parsedRelativeDate).format(DATE_FORMAT_NO_TIME);
      return { formatted: `"${computedDate}"`, literal: computedDate };
    }
  }
  return maybeWrapQuotes({ arg: rawArg, columnType });
}

function jinjaArg(arg: string | string[]): string {
  return `{{${arg}}}`;
}

/**
 * Intermediate shape used while building filter HQL: tracks both the
 * Python/Jinja string-literal source (`arg`) for non-HISC substitution and
 * the typed raw `literal` for HISC, alongside the hex-sl type. The `literal`
 * field is a placeholder for jinja-reference params (they get filtered out
 * before `rawLiterals` is populated in the final
 * {@link HqlNextTemporaryParamMapping}).
 */
type BuilderTemporaryParamMapping = Record<
  string,
  {
    arg: string;
    literal: string | number | boolean;
    type: HexSLTypes.DataType;
  }
>;

function getTemporaryParamName(
  arg: string,
  columnType: FilledDynamicValueTableColumnType,
): string {
  return `param_${hashObject({ arg, columnType })}`;
}

function getTemporaryParamType(
  columnType: FilledDynamicValueTableColumnType,
): FilledDynamicValueTableColumnType {
  // For number/boolean column types, we assume that the filter argument is the
  // correct type and does not need to be casted. However, for date/datetime
  // column types, the argument is just a string and need to be casted after
  // jinja parameterization.
  if (columnType === "NUMBER" || columnType === "BOOLEAN") {
    return columnType;
  }
  return "STRING";
}

function getQuotedColumn(column: string): string {
  return `\`${column}\``;
}

function getCalcExprAndLumpFromFilter({
  columnType,
  dateTransforms,
  filter,
  jinjaReferencedParams,
  paramMappingExcludeJinja,
  temporaryParamMapping,
  variableRefParamsOut,
}: {
  filter: Filter;
  columnType: FilledDynamicValueTableColumnType;
  jinjaReferencedParams: string[];
  /** Collects jinja variable references (e.g., "string_var" from "{{string_var}}") */
  variableRefParamsOut: string[];
  temporaryParamMapping: BuilderTemporaryParamMapping;
  paramMappingExcludeJinja?: boolean;
  dateTransforms: DateFilterTransformations;
}): { expr: string; lump?: Omit<HexSLTypes.WhereLump, "order_by"> } {
  const quotedColumn = getQuotedColumn(filter.column);
  // TODO(explore-hql-next): add validation for valid column types + filter operations
  if (FilterCellUnaryColumnPredicate.guard(filter.operation)) {
    switch (filter.operation.op) {
      case "IS_TRUE":
        return { expr: `${quotedColumn} = True` };
      case "IS_FALSE":
        return { expr: `${quotedColumn} = False` };
      case "ALWAYS":
        // what is always ??
        throw new Error("Not implemented");
      case "IS_NULL":
        return { expr: `IsNull(${quotedColumn})` };
      case "NOT_NULL":
        return { expr: `!IsNull(${quotedColumn})` };
      case "IS_EMPTY":
        // what is is empty? do we still use this?
        throw new Error("Not implemented");
      case "NOT_EMPTY":
        // what is is empty? do we still use this?
        throw new Error("Not implemented");
      default:
        assertNever(filter.operation, filter.operation);
    }
  } else if (FilterCellBinaryColumnPredicate.guard(filter.operation)) {
    let processedOperation = filter.operation;
    const processed = dateTransforms.processBinaryColumnPredicate({
      operation: filter.operation.op,
      arg: filter.operation.arg,
    });
    processedOperation = {
      ...filter.operation,
      op: processed.operation as any,
      arg: processed.arg,
    };

    const maybeJinjaReferencedParam = getSingleJinjaVariableReference(
      processedOperation.arg,
    );

    const {
      column: maybeDateCastedColumn,
      columnType: maybeDateCastedColumnType,
    } = dateTransforms.maybeDateCastColumn({
      args: [processedOperation.arg],
      column: quotedColumn,
      columnType,
      operation: processedOperation.op,
    });

    const argResult: FormattedFilterArg =
      maybeJinjaReferencedParam !== undefined
        ? {
            // For variable refs, `formatted` is the variable name itself;
            // `literal` is a placeholder (refs never reach `rawLiterals`).
            formatted: maybeJinjaReferencedParam,
            literal: maybeJinjaReferencedParam,
          }
        : formatArg({
            arg: processedOperation.arg,
            columnType: maybeDateCastedColumnType,
          });
    const formattedArg = argResult.formatted;
    const paramRef =
      maybeJinjaReferencedParam ??
      getTemporaryParamName(formattedArg, columnType);

    if (maybeJinjaReferencedParam !== undefined) {
      // This is a jinja variable reference - track separately for HISC
      variableRefParamsOut.push(paramRef);
    }

    if (!paramMappingExcludeJinja || maybeJinjaReferencedParam === undefined) {
      // Add to temporaryParamMapping for type information (used by hex-sl).
      // For variable refs, formattedArg is the variable name itself.
      temporaryParamMapping[paramRef] = {
        arg: formattedArg,
        literal: argResult.literal,
        type: columnTypeToHqlNextType(
          getTemporaryParamType(maybeDateCastedColumnType),
        ),
      };
    }

    jinjaReferencedParams.push(paramRef);

    const relativeDateUnit = tryParseRelativeDateOffsetValue(
      processedOperation.arg,
    );

    const jinjaParam = dateTransforms.castDateOrDatetimeArg({
      arg: jinjaArg(paramRef),
      columnType: maybeDateCastedColumnType,
      relativeDateTruncUnit:
        // truncate to the unit specified if the amount being offset is 0,
        // so that we can filter by the current year/month/week/day.
        relativeDateUnit?.amount === 0 ? relativeDateUnit.unit : null,
    });
    const matchCase = filter.matchCase ?? columnType !== "STRING";

    switch (processedOperation.op) {
      case "DATE_AFTER":
      case "DATE_BEFORE":
      case "DATE_EQUAL_OR_BEFORE":
      case "DATE_EQUAL_OR_AFTER":
      case "DATE_NOT_EQUAL": {
        return {
          expr: `${maybeDateCastedColumn} ${DATE_OPERATOR_MAP[processedOperation.op]} ${jinjaParam}`,
        };
      }
      case "DATE_EQUAL":
        const equalRewritten = dateTransforms.maybeRewriteDateEqual({
          columnType: maybeDateCastedColumnType,
          arg: processedOperation.arg,
          hasJinja: maybeJinjaReferencedParam !== undefined,
        });
        if (!equalRewritten) {
          return {
            expr: `${maybeDateCastedColumn} ${DATE_OPERATOR_MAP[processedOperation.op]} ${jinjaParam}`,
          };
        }
        // Reuse the existing paramRef for the lower bound but overwrite its value
        // to the computed lowerArg boundary, and do not apply truncation to the arg.
        const lowerResult = formatArg({
          arg: equalRewritten.lowerArg,
          columnType: maybeDateCastedColumnType,
        });
        temporaryParamMapping[paramRef] = {
          arg: lowerResult.formatted,
          literal: lowerResult.literal,
          type: columnTypeToHqlNextType(getTemporaryParamType(columnType)),
        };

        const lowerJinjaParam = dateTransforms.castDateOrDatetimeArg({
          arg: jinjaArg(paramRef),
          columnType: maybeDateCastedColumnType,
          relativeDateTruncUnit: null,
        });

        const upperResult = formatArg({
          arg: equalRewritten.upperArg,
          columnType: maybeDateCastedColumnType,
        });
        const upperParamRef = getTemporaryParamName(
          upperResult.formatted,
          columnType,
        );
        temporaryParamMapping[upperParamRef] = {
          arg: upperResult.formatted,
          literal: upperResult.literal,
          type: columnTypeToHqlNextType(getTemporaryParamType(columnType)),
        };
        jinjaReferencedParams.push(upperParamRef);

        const upperJinjaParam = dateTransforms.castDateOrDatetimeArg({
          arg: jinjaArg(upperParamRef),
          columnType: maybeDateCastedColumnType,
          relativeDateTruncUnit: null,
        });

        return {
          expr: `${maybeDateCastedColumn} >= ${lowerJinjaParam} AND ${maybeDateCastedColumn} < ${upperJinjaParam}`,
        };
      case "GT":
        return { expr: `${quotedColumn} > ${jinjaParam}` };
      case "GTE":
        return { expr: `${quotedColumn} >= ${jinjaParam}` };
      case "EQ":
      case "NEQ": {
        const operator = processedOperation.op === "EQ" ? "=" : "!=";

        if (!matchCase) {
          return {
            expr: `lower(${quotedColumn}) ${operator} lower(${jinjaParam})`,
          };
        }
        return { expr: `${quotedColumn} ${operator} ${jinjaParam}` };
      }
      case "LTE":
        return { expr: `${quotedColumn} <= ${jinjaParam}` };
      case "LT":
        return { expr: `${quotedColumn} < ${jinjaParam}` };
      case "CONTAINS":
      case "NOT_CONTAINS": {
        const expr = matchCase
          ? `contains(${quotedColumn}, ${jinjaParam})`
          : `contains(lower(${quotedColumn}), lower(${jinjaParam}))`;
        if (processedOperation.op === "CONTAINS") {
          return { expr };
        }
        return { expr: `!${expr}` };
      }
      default:
        assertNever(processedOperation.op, processedOperation.op);
    }
  } else if (FilterCellListBinaryColumnPredicate.guard(filter.operation)) {
    const {
      column: maybeDateCastedColumn,
      columnType: maybeDateCastedColumnType,
      truncationApplied,
    } = dateTransforms.maybeDateCastColumn({
      args: filter.operation.arg,
      column: quotedColumn,
      columnType,
      operation: filter.operation.op,
    });

    const params = filter.operation.arg.map((arg) => {
      const maybeJinjaReferencedParam = getSingleJinjaVariableReference(arg);

      const argResult: FormattedFilterArg =
        maybeJinjaReferencedParam !== undefined
          ? {
              formatted: maybeJinjaReferencedParam,
              literal: maybeJinjaReferencedParam,
            }
          : formatArg({
              arg,
              columnType: maybeDateCastedColumnType,
            });
      const formattedArg = argResult.formatted;
      const paramRef =
        maybeJinjaReferencedParam ??
        getTemporaryParamName(formattedArg, columnType);

      if (maybeJinjaReferencedParam !== undefined) {
        // This is a jinja variable reference - track separately for HISC
        variableRefParamsOut.push(paramRef);
      }

      if (
        !paramMappingExcludeJinja ||
        maybeJinjaReferencedParam === undefined
      ) {
        // Add to temporaryParamMapping for type information (used by hex-sl).
        // For variable refs, formattedArg is the variable name itself.
        temporaryParamMapping[paramRef] = {
          arg: formattedArg,
          literal: argResult.literal,
          type: columnTypeToHqlNextType(
            getTemporaryParamType(maybeDateCastedColumnType),
          ),
        };
      }

      jinjaReferencedParams.push(paramRef);
      const jinjaWrappedArg = jinjaArg(paramRef);

      return dateTransforms.castDateOrDatetimeArg({
        arg: jinjaWrappedArg,
        columnType: maybeDateCastedColumnType,
        // used in range, so not specifying trunc unit; if specified, can result in
        // different truncation levels for each arg which would result in incorrect
        // filter behavior
        relativeDateTruncUnit: null,
      });
    });

    switch (filter.operation.op) {
      case "IS_ONE_OF":
      case "NOT_ONE_OF": {
        const isOneOf = `IsOneOf(${quotedColumn}, ${params.join(", ")})`;
        if (filter.operation.op === "IS_ONE_OF") {
          return { expr: isOneOf };
        } else {
          return { expr: `!${isOneOf}` };
        }
      }
      case "DATE_BETWEEN": {
        if (filter.operation.arg.length !== 2) {
          return { expr: "" };
        }

        const normalized = dateTransforms.normalizeBetweenBounds?.({
          args: filter.operation.arg,
          columnType,
          truncationApplied: truncationApplied ?? false,
        });

        const lowerOp = normalized.lowerInclusive ? ">=" : ">";
        const upperOp = normalized.upperInclusive ? "<=" : "<";

        let upperExpr: string;
        if (normalized.upperArg === filter.operation.arg[1]) {
          // Reuse existing bound expr
          upperExpr = params[1]!;
        } else {
          // Create a new param for the normalized upper bound
          const upperResult = formatArg({
            arg: normalized.upperArg,
            columnType: maybeDateCastedColumnType,
          });
          const upperParamRef = getTemporaryParamName(
            upperResult.formatted,
            columnType,
          );
          temporaryParamMapping[upperParamRef] = {
            arg: upperResult.formatted,
            literal: upperResult.literal,
            type: columnTypeToHqlNextType(
              getTemporaryParamType(maybeDateCastedColumnType),
            ),
          };
          jinjaReferencedParams.push(upperParamRef);

          upperExpr = dateTransforms.castDateOrDatetimeArg({
            arg: jinjaArg(upperParamRef),
            columnType: maybeDateCastedColumnType,
            relativeDateTruncUnit: null,
          });
        }
        return {
          expr: `${maybeDateCastedColumn} ${lowerOp} ${params[0]} AND ${maybeDateCastedColumn} ${upperOp} ${upperExpr} `,
        };
      }
      default:
        assertNever(filter.operation, filter.operation);
    }
  } else if (LumpColumnPredicate.guard(filter.operation)) {
    const lump = createWhereLumpFromFilter(filter);
    return {
      expr: lump?.predicate ?? "",
      lump,
    };
  } else {
    assertNever(filter.operation, filter.operation);
  }
}

export function getWhereLumpsFromFilterGroup(
  group: FilterGroup,
): Omit<HexSLTypes.WhereLump, "order_by">[] {
  if (
    group == null ||
    group.filters.filter(filterFullyConfigured).length === 0
  ) {
    return [];
  }

  return group.filters
    .filter(filterFullyConfigured)
    .flatMap((filterOrGroup) => {
      if (FilterGroup.guard(filterOrGroup)) {
        return getWhereLumpsFromFilterGroup(filterOrGroup);
      } else if (Filter.guard(filterOrGroup)) {
        const columnType = filterOrGroup.columnType;
        if (columnType == null) {
          return;
        }

        if (FilterCellLumpColumnPredicate.guard(filterOrGroup.operation)) {
          return createWhereLumpFromFilter(filterOrGroup);
        }
      }
    })
    .filter(notNullish);
}

// TODO(explore-hql-next): maybe validate the generated calc?
// Exported for testing
/**
 * Creates a single consolidated expression from multiple filters in a filter group
 * and keeps track of the first lump filter in the group if there is one
 */
export function getCalcExprAndLumpFromFilterGroup(
  group: FilterGroup,
  jinjaReferencedParams: string[],
  temporaryParamMapping: BuilderTemporaryParamMapping,
  paramMappingExcludeJinja?: boolean,
  options?: FilterHqlOptions,
  variableRefParamsOut: string[] = [],
): { expr: string | null; lump?: Omit<HexSLTypes.WhereLump, "order_by"> } {
  if (
    group == null ||
    group.filters.filter(filterFullyConfigured).length === 0
  ) {
    return { expr: null, lump: undefined };
  }

  const dateTransforms = getDateFilterTransformations(options?.dateFilterMode);

  const calcExprsAndLump = group.filters
    .filter(filterFullyConfigured)
    .map((filterOrGroup) => {
      if (FilterGroup.guard(filterOrGroup)) {
        return getCalcExprAndLumpFromFilterGroup(
          filterOrGroup,
          jinjaReferencedParams,
          temporaryParamMapping,
          paramMappingExcludeJinja,
          options,
          variableRefParamsOut,
        );
      } else if (Filter.guard(filterOrGroup)) {
        const columnType = filterOrGroup.columnType;
        if (columnType == null) {
          return;
        }
        return getCalcExprAndLumpFromFilter({
          filter: filterOrGroup,
          columnType,
          jinjaReferencedParams,
          variableRefParamsOut,
          temporaryParamMapping,
          paramMappingExcludeJinja,
          dateTransforms,
        });
      }
    })
    .filter(notNullish);

  if (calcExprsAndLump.length === 0) {
    return { expr: null, lump: undefined };
  }
  if (calcExprsAndLump.length === 1) {
    return {
      expr: calcExprsAndLump[0]!.expr,
      lump: calcExprsAndLump[0]!.lump,
    };
  } else {
    return {
      expr: calcExprsAndLump.map((f) => f.expr).join(` ${group.operation} `),
      lump: calcExprsAndLump[0]!.lump,
    };
  }
}

function getFilterUniqueKey(filter: ColumnFilter): string {
  return filter.column + filter.queryPath?.join("");
}

function createWhereLumpFromFilter(
  filter: Filter,
): Omit<HexSLTypes.WhereLump, "order_by"> | undefined {
  if (!FilterCellLumpColumnPredicate.guard(filter.operation)) {
    return undefined;
  }

  const orderDirection =
    filter.operation.op === "TOP_N" || filter.operation.op === "NOT_TOP_N"
      ? "desc"
      : "asc";

  const derivedPredicateOp =
    filter.operation.op === "TOP_N" || filter.operation.op === "BOTTOM_N"
      ? "LTE"
      : "GT";

  return {
    lump_by: `\`${filter.column}\``,
    window_function: "row_number",
    order_direction: orderDirection,
    predicate: getLumpPredicateString({
      op: derivedPredicateOp,
      arg: Number(filter.operation.arg),
    }),
  };
}

export type HqlParamMapping = Record<string, null | boolean | string | number>;

export function getHqlParamMapping(filters: ColumnFilter[] | null) {
  const paramMapping: HqlParamMapping = {};
  if (filters == null || filters.length === 0) return paramMapping;
  const completeFilters = filters.filter(columnFilterFullyConfigured);
  if (completeFilters.length === 0) return paramMapping;

  // using this logic to build the temporaryParamMapping, ignore the params and exprs
  const params: string[] = [];
  const temporaryParamMapping: BuilderTemporaryParamMapping = {};
  completeFilters.forEach((filter) => {
    const filterOrFilterGroup = displayTableFilterToFilterCellFilter(filter);
    getCalcExprAndLumpFromFilterGroup(
      {
        operation: "AND",
        filters: [filterOrFilterGroup],
      },
      params,
      temporaryParamMapping,
      true, // exclude jinja params from paramMapping so kernel will lookup in scope
    );
  });

  // `literal` is already the typed JS-native value (see `BuilderTemporaryParamMapping`);
  // we just need to preserve `null` for the null sentinel type, which no filter
  // flow produces today but the schema allows.
  Object.entries(temporaryParamMapping).forEach(([key, { literal, type }]) => {
    paramMapping[key] = type === "null" ? null : literal;
  });

  return paramMapping;
}

export function addFiltersToHqlBuilder({
  aliasToOriginalColumnName,
  builder,
  exploreSpec,
  filters,
  options,
}: {
  builder: HqlSelectionBuilder | HqlAggregationBuilder;
  filters: ColumnFilter[] | null;
  aliasToOriginalColumnName: Record<string, string>;
  exploreSpec?: ExploreSpec;
  options?: FilterHqlOptions;
}): void {
  if (filters == null || filters.length === 0) {
    return;
  }

  const originalColumnNameToAlias: Record<string, string> = {};
  Object.entries(aliasToOriginalColumnName).forEach(([key, value]) => {
    originalColumnNameToAlias[value] = key;
  });

  // Filter out incomplete filters first
  const completeFilters = filters.filter(columnFilterFullyConfigured);

  // Separate dimensions and measures/aggregated dimensions into WHERE and HAVING
  const dimensionFilters: ColumnFilter[] = [];
  const measureFilters: ColumnFilter[] = [];
  completeFilters.forEach((filter) => {
    if (
      filter.fieldType === ExploreFieldType.MEASURE ||
      filter.aggregation != null
    ) {
      measureFilters.push(filter);
    } else {
      dimensionFilters.push(filter);
    }
  });

  // Dimension filters (WHERE clause) - include WhereExprs and WhereLumps
  if (dimensionFilters.length > 0) {
    const params: string[] = [];
    const variableRefParams: string[] = [];
    const temporaryParamMapping: BuilderTemporaryParamMapping = {};

    dimensionFilters.forEach((filter) => {
      // create WhereExpr filters
      const filterOrFilterGroup = displayTableFilterToFilterCellFilter(filter);
      const { expr, lump } = getCalcExprAndLumpFromFilterGroup(
        {
          operation: "AND",
          filters: [filterOrFilterGroup],
        },
        params,
        temporaryParamMapping,
        undefined,
        options,
        variableRefParams,
      );
      if (expr) {
        const column = {
          value: filter.column,
          dataType: filter.columnType
            ? columnTypeToCalciteType(filter.columnType)
            : "VARCHAR",
          queryPath: filter.queryPath,
          fieldType: filter.fieldType ?? ExploreFieldType.COLUMN,
        };
        const dataset = getDatasetPathForField(column);
        // if the filter group contains a lump
        // generate a WhereLump style HQL filter with any other
        // filters in the group combined into the predicate expr
        // if there's no lump, just use the filter expr as is
        const resolvedLumpOrderBy = resolveLumpOrderBy(
          filter.lumpOrderBy,
          undefined,
          exploreSpec,
        );
        const hqlFilter =
          lump != null && resolvedLumpOrderBy != null
            ? {
                ...lump,
                order_by: resolvedLumpOrderBy,
                predicate: expr
                  ? expr.replace(
                      getQuotedColumn(filter.column),
                      "original_value",
                    )
                  : lump.predicate,
                dataset,
              }
            : { expr, dataset };
        builder.filter(hqlFilter);
      }
    });

    // NOTE: historically, we only really support jinja params when filtering on
    // string columns/generally treat the jinja params as strings, so am just
    // specifying these as string types for hex-sl.
    //
    // NOTE 2: Calling addParams multiple times (here and below for HAVING) merges the parameters.
    // If the same parameter name exists in both sets with potentially different inferred types,
    // the last one called (HAVING) will overwrite the type.
    if (params.length > 0) {
      // Build literals mapping, excluding variable refs (they stay as jinja syntax
      // for kernel resolution in non-HISC, or become Scalar.fromVariable in HISC)
      const variableRefParamsSet = new Set(variableRefParams);
      const nonRefEntries = typedObjectEntries(temporaryParamMapping).filter(
        ([param]) => !variableRefParamsSet.has(param),
      );
      const hqlParamMapping: HqlNextTemporaryParamMapping = {
        literals: typedObjectFromEntries(
          nonRefEntries.map(([param, value]) => [param, value.arg]),
        ),
        rawLiterals: typedObjectFromEntries(
          nonRefEntries.map(([param, value]) => [param, value.literal]),
        ),
        jinjaRefs: variableRefParams,
      };
      builder.addParams(
        typedObjectFromEntries(
          params.map((param) => [
            param,
            temporaryParamMapping[param]?.type ?? ("string" as const),
          ]),
        ),
        hqlParamMapping,
      );
    }
  }

  // Measure filters (HAVING clause)
  if (measureFilters.length > 0 && isAggregationBuilder(builder)) {
    const columnExprMap: Record<string, string[]> = {};
    const params: string[] = [];
    const variableRefParams: string[] = [];
    const temporaryParamMapping: BuilderTemporaryParamMapping = {};

    measureFilters.forEach((filter) => {
      let aliasedName = originalColumnNameToAlias[filter.column];

      // Handle if there's the `_<agg>` suffix
      if (aliasedName == null && filter.aggregation != null) {
        const aggregatedColumnId = getExploreFieldName({
          value: filter.column,
          queryPath: filter.queryPath ?? [],
          fieldType: filter.fieldType ?? ExploreFieldType.COLUMN,
          aggregation: filter.aggregation,
          truncUnit: undefined,
        });
        aliasedName = originalColumnNameToAlias[aggregatedColumnId];
      }

      if (!aliasedName) {
        // Ignore the filter; this must be a multi-series chart
        return;
      }

      const filterToProcess = {
        ...filter,
        column: aliasedName as DisplayTableColumnId,
      };

      const filterOrFilterGroup =
        displayTableFilterToFilterCellFilter(filterToProcess);

      const { expr } = getCalcExprAndLumpFromFilterGroup(
        {
          operation: "AND",
          filters: [filterOrFilterGroup],
        },
        params,
        temporaryParamMapping,
        undefined,
        options,
        variableRefParams,
      );

      if (expr) {
        columnExprMap[getFilterUniqueKey(filter)] ??= [];
        columnExprMap[getFilterUniqueKey(filter)]!.push(expr);
      }
    });

    // Add params for having clauses too
    if (params.length > 0) {
      // Build literals mapping, excluding variable refs (they stay as jinja syntax
      // for kernel resolution in non-HISC, or become Scalar.fromVariable in HISC)
      const variableRefParamsSet = new Set(variableRefParams);
      const nonRefEntries = typedObjectEntries(temporaryParamMapping).filter(
        ([param]) => !variableRefParamsSet.has(param),
      );
      const hqlParamMapping: HqlNextTemporaryParamMapping = {
        literals: typedObjectFromEntries(
          nonRefEntries.map(([param, value]) => [param, value.arg]),
        ),
        rawLiterals: typedObjectFromEntries(
          nonRefEntries.map(([param, value]) => [param, value.literal]),
        ),
        jinjaRefs: variableRefParams,
      };
      builder.addParams(
        typedObjectFromEntries(
          params.map((param) => [
            param,
            temporaryParamMapping[param]?.type ?? ("string" as const),
          ]),
        ),
        hqlParamMapping,
      );
    }

    // Add measure filters to builder
    measureFilters.forEach((f) => {
      const exprList = columnExprMap[getFilterUniqueKey(f)];
      if (exprList && exprList.length > 0) {
        const havingExpr = exprList.join(" AND ");
        builder.having({ expr: havingExpr }); // Pass only expr, column is unused
      }
    });
  }
}
